/**
 * RulingsSectionSkeleton - Loading skeleton for rulings section
 *
 * Displays placeholder content while rulings data is being fetched.
 */
import type { ReactElement } from 'react';
import { IoInformationCircle } from 'react-icons/io5';

/**
 * Inner content skeleton for ruling items (no wrapper)
 * Used inside RulingsSection when data is loading
 */
export function RulingsContentSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="border-storm border-b pb-3 last:border-b-0 last:pb-0">
          {/* Date and badge */}
          <div className="mb-1 flex items-center gap-2">
            <div className="bg-slate h-3 w-20 animate-pulse rounded" />
            {i === 0 && <div className="bg-slate h-4 w-10 animate-pulse rounded" />}
          </div>
          {/* Comment text */}
          <div className="space-y-1.5">
            <div className="bg-slate h-3.5 w-full animate-pulse rounded" />
            <div className="bg-slate h-3.5 w-4/5 animate-pulse rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Full section skeleton with wrapper
 * Used when the entire section needs a placeholder
 */
export default function RulingsSectionSkeleton(): ReactElement {
  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-ash flex items-center gap-2 text-xs font-medium tracking-widest uppercase">
            <IoInformationCircle className="h-4 w-4" />
            Rulings
          </h3>
        </div>

        <RulingsContentSkeleton />
      </div>
    </section>
  );
}
